import { create, type StateCreator } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type { AutocompleteOption } from "@/components/ui/molecules/autocomplete/Autocomplete.type";
import type { ResultOf } from "@/data-access/graphql";
import type {
  NewsAlertScheduleFrequency,
  NewsAlertSortOrder,
} from "@/data-access/news-alerts/queries/news-alerts-queries";
import type { GetScheduledItems } from "@/data-access/news-alerts/queries/scheduler-items-queries";
import type { TimezoneChoices } from "@/data-access/news-alerts/queries/timezone-queries";

export type ScheduleType = ResultOf<
  typeof GetScheduledItems
>["schedulerItems"][number]["scheduleType"];

export type TableAlert = {
  id: string;
  name: string;
  schedule: string; // e.g., "Daily at 8:00 AM CT"
  recipients: number;
  lastUpdated: string; // e.g., "Aug 14, 2025"
  createdBy: string; // e.g., "Marsha Langman"
  scheduleType: ScheduleType;
};

export type AlertSection = {
  id: string;
  name: string;
  feedId: string | null;
  savedViewId: string | null;
  articleLimit: number;
  sortOrder: NewsAlertSortOrder;
};

export type AlertCreationAndEditState = {
  // Details
  id: string | null;
  alertName: string;
  description: string;

  // Schedule
  scheduleFrequency: NewsAlertScheduleFrequency;
  scheduleTime: string;
  timezone: TimezoneChoices | "";

  // Recipients
  recipients: Set<string>;

  // Sections (Map with section ID as key)
  sections: Map<string, AlertSection>;

  // Logo
  logoFile: File | null;
  logoPreviewUrl: string | null;
  logoId: string | null;
};

type NewAlertModalState = {
  isCreateNewsAlertModalOpen: boolean;
  isCreateNewsAlertSectionModalOpen: boolean;
  isDeleteNewsAlertModalOpen: boolean;
  isEditNewsAlertModalOpen: boolean;
};

type NewsAlertManagementState = {
  deleteNewsAlertId: string | null;
  editNewsAlertId: string | null;
  isDeletingNewsAlert: boolean;
  isEditingNewsAlert: boolean;
  isCreatingNewsAlert: boolean;
  isCreatingNewsAlertSection: boolean;
  isEditingNewsAlertSection: boolean;
  isFetchingNewsAlertById: boolean;
  createEditNewsAlert: AlertCreationAndEditState;
  createEditNewsAlertSection: AlertSection;
  savedViewOptions: AutocompleteOption[];
  isFetchingSavedViews: boolean;
  isGeneratingNewsAlertPreview: boolean;
  isUploadingNewsAlertLogo: boolean;
};

type NewsAlertsSlice = NewAlertModalState & NewsAlertManagementState;

const createNewsAlertsSlice: StateCreator<NewsAlertsSlice, [], []> = () => ({
  isCreateNewsAlertModalOpen: false,
  isCreateNewsAlertSectionModalOpen: false,
  isDeleteNewsAlertModalOpen: false,
  isEditNewsAlertModalOpen: false,
  editNewsAlertId: null,
  deleteNewsAlertId: null,
  isDeletingNewsAlert: false,
  isEditingNewsAlert: false,
  isEditingNewsAlertSection: false,
  isCreatingNewsAlert: false,
  isCreatingNewsAlertSection: false,
  isFetchingNewsAlertById: false,
  savedViewOptions: [],
  isFetchingSavedViews: false,
  isGeneratingNewsAlertPreview: false,
  isUploadingNewsAlertLogo: false,
  createEditNewsAlert: {
    id: null,
    alertName: "",
    description: "",
    scheduleFrequency: "DAILY",
    scheduleTime: "",
    timezone: "",
    recipients: new Set(),
    sections: new Map(),
    logoFile: null,
    logoPreviewUrl: null,
    logoId: null,
  },
  createEditNewsAlertSection: {
    id: "",
    name: "",
    feedId: null,
    savedViewId: null,
    articleLimit: 25,
    sortOrder: "DATE_DESC" as NewsAlertSortOrder,
  },
});

const useNewsAlertsStore = create<NewsAlertsSlice>()(
  devtools(createNewsAlertsSlice, {
    name: "news-alerts-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useNewsAlertsStore;
